import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Table from "../../../designsystem/KC_Table/KC_Table";
import moment from "moment-timezone";
import {
  DEFAULT_CURRENCY_SUB_UNITS,
  DEFAULT_CURRENCY
} from "@tootsweet/model/enums/Currencies";
import { tt } from "../../../utils/i18n/i18n";
import TSUtils from "../../../utils/helpers/TSUtils";
import React, { useMemo } from "react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { ManagedAccountSubscription } from "@tootsweet/model/entities/ManagedAccount";
import { useDispatch } from "react-redux";
import {
  setLoading,
  useSubscriptionPlansSelector
} from "../../../redux/actions/app";
import AxiosRaw, { AxiosResponse } from "axios";
import CONFIG from "../../../model/TSConfig";
import ReturnCodes from "../../../utils/error_handling/ReturnCodes";
import NiceModal from "@ebay/nice-modal-react";
import PostPaymentModal from "../../../components/modal/ConfirmPayment/PostPaymentModal";
import fetchAllManagedAccounts from "../../../api/fetchAllManagedAccounts";
import UnsubscribeConfirmationModal from "./UnsubscriptionModals/UnsubscribeConfirmationModal";
import UnsubscribeReasonsModal, {
  type UnsubscriptionReason
} from "./UnsubscriptionModals/UnsubscribeReasonsModal";
import UnsubscribeSuccessModal from "./UnsubscriptionModals/UnsubscribeSuccessModal";
import { useHistory } from "react-router-dom";

export default function () {
  const dispatch = useDispatch();
  const plans = useSubscriptionPlansSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const history = useHistory();

  const subs = useMemo<ManagedAccountSubscription[]>(() => {
    if (!accountToDisplay?.subs) {
      return [];
    }

    const res = [...accountToDisplay.subs];

    res.sort((a, b) => {
      return b.endTs - a.endTs;
    });

    return res;
  }, [accountToDisplay]);

  const unsubscribe = async (sub: ManagedAccountSubscription) => {
    try {
      if (!accountToDisplay?.token) {
        return;
      }

      // First modal: confirmation
      const confirmUnsubscribe: boolean = await NiceModal.show(
        UnsubscribeConfirmationModal
      );

      if (!confirmUnsubscribe) {
        return;
      }

      // Second modal: reasons selection
      const selectedReasons: UnsubscriptionReason[] | null =
        await NiceModal.show(UnsubscribeReasonsModal, {});

      if (!selectedReasons) {
        return;
      }

      dispatch(setLoading(true));

      const res: AxiosResponse<{
        code: ReturnCodes;
        endDate: string;
        isImmediate?: boolean;
      }> = await AxiosRaw({
        method: "put",
        url: `${CONFIG.saasBackendUrl}/subscription/unsubscribe`,
        headers: {
          Authorization: `Bearer ${accountToDisplay.token}`,
          "Content-Type": "application/json"
        },
        params: {
          subscriptionId: sub._id
        },
        data: {
          reasons: selectedReasons.map((r: UnsubscriptionReason) => ({
            key: r.key,
            label: r.label
          }))
        }
      });

      const isOk = res.data.code === ReturnCodes.Ok;

      dispatch(setLoading(false));

      if (isOk) {
        // Third modal: success confirmation
        await NiceModal.show(UnsubscribeSuccessModal, {
          endDate: moment(res.data.endDate).format(tt("date_format_full_date")),
          history,
          isImmediate: res.data.isImmediate,
          accountToDisplay
        });

        dispatch(fetchAllManagedAccounts({}));
      } else {
        await NiceModal.show(PostPaymentModal, {
          headerText: tt("error"),
          bodyText: tt("error"),
          buttonText: tt("ok")
        });
      }
    } catch (err) {
      console.error(err);
      dispatch(setLoading(false));
      await NiceModal.show(PostPaymentModal, {
        headerText: tt("error"),
        bodyText: tt("error"),
        buttonText: tt("ok")
      });
    }
  };

  const buildDisplayNameForSubscription = (sub: ManagedAccountSubscription) => {
    let duration = "";

    switch (sub.duration) {
      case 0:
      case 1:
        duration = tt("sub_duration_monthly");
        break;
      case 12:
        duration = tt("sub_duration_yearly");
        break;
      default:
        duration = tt("months", {
          duration: sub.duration
        });
        break;
    }

    if (!sub.planId) {
      return `${tt("subscription")} ${duration}`;
    }

    const plan = plans?.find((p) => p._id === sub.planId);

    if (plan) {
      return `${tt("subscription")} ${plan.name} ${duration}`;
    }

    return `${tt("subscription")} ${duration}`;
  };

  return (
    <>
      {!subs.length && (
        <KC_UIText weight="reg" text={tt("noActiveSubscriptionsMessage")} />
      )}
      {!!subs.length && (
        <KC_Table
          id="subscription-table"
          keyPrefix="subs"
          headers={
            <>
              <th>{tt("establishmentTableHeader")}</th>
              <th>{tt("typeTableHeader")}</th>
              <th>{tt("start_date")}</th>
              <th>{tt("renewal_date")}</th>
              <th>{tt("end_date")}</th>
              <th>{tt("monthly_ht")}</th>
              <th>{tt("actionsTableHeader")}</th>
            </>
          }
          children={
            <>
              {subs.map((sub, idx) => {
                const willRenew = sub.renew && !sub.interrupted;
                return (
                  <tr key={`sub-${idx}`}>
                    <td>{sub.placeName}</td>
                    <td>{buildDisplayNameForSubscription(sub)}</td>
                    <td>
                      {moment(sub.startTs, "X")
                        .tz("UTC", true)
                        .format(tt("display_date_format"))}
                    </td>
                    <td>
                      {willRenew &&
                        moment(sub.rTs, "X")
                          .tz("UTC", true)
                          .format(tt("display_date_format"))}
                    </td>
                    <td>
                      {!willRenew &&
                        moment(sub.rTs, "X")
                          .tz("UTC", true)
                          .format(tt("display_date_format"))}
                    </td>
                    <td>
                      {TSUtils.formatAmount(
                        sub.amountExclVat,
                        accountToDisplay?.curSubUnits ||
                          DEFAULT_CURRENCY_SUB_UNITS,
                        false,
                        accountToDisplay?.currency || DEFAULT_CURRENCY
                      )}
                    </td>
                    <td>
                      {willRenew && (
                        <KC_UIText
                          weight="reg"
                          text={tt("cancelSubscriptionButton")}
                          className="cursor-pointer underline kc-grey"
                          onClick={() => {
                            void unsubscribe(sub);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          }
        />
      )}
    </>
  );
}
